import React, { useContext, useEffect, useState } from "react"

import tw from "twin.macro"
import { Card } from "../../../atoms/Card"
import { Badge } from "../../../atoms/Badge"
import Icon from "../../../atoms/Icon"
import { Link } from "../../../atoms/Link"
import LeaseCard from "../../../molecules/OfferCard/LeaseCard"
import FinanceCard from "../../../molecules/OfferCard/FinanceCard"
import CashCard from "../../../molecules/OfferCard/CashCard"
import { TWColor } from "../../../../global"
import { DealerDetailsPageContext } from "../../../../templates/dealerDetails"
import { toUsd } from "../../../../helpers"
import { LanguageContext } from "../../../../contexts/Language"
import { FavoritesContext } from "../../../../contexts/Favorites/context"
import {
  addFavoritedOffer,
  removeFavoritedOffer,
  setFavoriteMessage,
} from "../../../../contexts/Favorites/actions"
import useVehicleData from "../../../../hooks/Tealium/useVehicleData"
import LeaseCashCard from "../../../molecules/OfferCard/LeaseCashCard"
import FinanceCashCard from "../../../molecules/OfferCard/FinanceCashCard"
import { FamilyOfferCardProps } from "./FamilyOffersSectionProps.d"
import { Image } from "../../../atoms/Image"

const FamilyOfferCard: React.FC<FamilyOfferCardProps> = ({
  offer,
  cardType,
  cardLabel,
  ...remainingProps
}) => {
  const {
    cardTitle,
    type,
    duration,
    monthlyPayment,
    dueAtSigningAmount,
    endDate,
    isExpiringSoon,
    cashAmount,
    termRates,
    series,
    _id,
    year,
    extraCashAmount,
    extraCashLabel,
  } = offer
  const { name } = series || {}
  const { dealer } = useContext(DealerDetailsPageContext)
  const [vehicleData] = useVehicleData()
  const { _, language } = useContext(LanguageContext)

  /* Sets Badge color based on card label */
  let badgeColor: TWColor
  switch (cardType) {
    case "APR":
      badgeColor = "green"
      break
    case "Lease":
      badgeColor = "blue"
      break
    case "Cash":
      badgeColor = "red-400"
      break
    case "Lease Cash":
      badgeColor = "blue"
      break
    case "Finance Cash":
      badgeColor = "green"
      break
    default:
      badgeColor = "red-400"
      break
  }

  // Concatenate "Rebate" to end of type for Cash offers
  let formattedModelName: string

  // Set aria label based on cardType
  let ariaCardLabel: string

  switch (cardType) {
    case "APR":
      formattedModelName = `${
        language === "en" ? year + " " + name : name + " " + year
      }`
      const sortedRates = termRates?.sort(
        (a: { rate: number }, b: { rate: number }) => a.rate - b.rate,
      )
      const lowestRates = sortedRates?.filter(
        offer => offer.rate == sortedRates[0].rate,
      )
      const sortedTermRate = lowestRates?.sort(
        (a: { term: number }, b: { term: number }) => b.term - a.term,
      )[0]

      ariaCardLabel = `${formattedModelName}, ${sortedTermRate?.rate}% ${type} for ${sortedTermRate?.term} months`
      break
    case "Lease":
      formattedModelName = `${
        language === "en" ? year + " " + name : name + " " + year
      }`
      ariaCardLabel = `Lease a new ${formattedModelName}, $${monthlyPayment} for ${duration} months`
      break
    case "Cash":
      formattedModelName = `${
        language === "en" ? year + " " + name : name + " " + year
      }`
      ariaCardLabel = `$${cashAmount} ${formattedModelName}`
      break
    case "Lease Cash":
      formattedModelName = `${
        language === "en" ? year + " " + name : name + " " + year
      }`
      ariaCardLabel = `$${cashAmount} ${formattedModelName}`
      break
    case "Finance Cash":
      formattedModelName = `${
        language === "en" ? year + " " + name : name + " " + year
      }`
      ariaCardLabel = `$${cashAmount} ${formattedModelName}`
      break
    default:
      formattedModelName = type!
      break
  }

  const [{ favoritedOffers }, dispatch] = useContext(FavoritesContext)
  const [isFavorited, setIsFavorited] = useState(false)

  useEffect(() => {
    if (!favoritedOffers || !Array.isArray(favoritedOffers)) return

    setIsFavorited(favoritedOffers.includes(_id))
  }, [favoritedOffers])
  return (
    <div
      css={[
        tw`relative w-72 mx-auto h-96	`,
        tw`md:(w-72)`,
        tw`lg:(w-[16rem])`,
        tw`xl:(w-72)`,
      ]}
    >
      <button
        onClick={() => {
          isFavorited
            ? dispatch(removeFavoritedOffer(_id!))
            : (dispatch(addFavoritedOffer(_id!)),
              dispatch(setFavoriteMessage("offers")))
        }}
        css={[
          tw`absolute top-[1.25rem] right-[0.5rem] lg:right-[0.5rem] lg:top-[1.25rem] z-10 flex items-center border border-transparent p-1 rounded-full hover:bg-gray-100`,
          tw`focus-visible:outline-gray-50`,
        ]}
        aria-label={
          isFavorited
            ? `Unfavorite ${cardTitle} ${cardType} Offer`
            : `Favorite ${cardTitle} ${cardType} Offer`
        }
      >
        <Icon.Heart
          color={isFavorited ? "red-400" : "gray-900"}
          css={[tw`w-6 mt-0.5 ml-auto`]}
          filled={isFavorited}
        />
      </button>
      <Link
        className="group"
        css={[tw`outline-none pt-0 pb-6 focus-visible:outline-none`]}
        to={language === "es" ? `/es/offers/${_id}` : `/offers/${_id}`}
        forceExternal={true} // Needed for expired offer redirect
        target="_self" // Needed for expired offer redirect
        aria-label={ariaCardLabel!}
      >
        <Card
          shadowLevel={0}
          css={[
            tw`relative w-[16rem] aspect-square bg-white mt-3 overflow-visible rounded-3xl group-hover:(ease-in-out transition-all duration-150) group-focus-visible:(outline-gray-50)`,
            tw`md:(w-72)`,
            tw`lg:(w-[16rem])`,
            tw`xl:(w-72)`,
          ]}
          {...remainingProps}
        >
          {/* BADGE AND HEART ICON */}
          <Badge
            color={badgeColor}
            css={[tw`absolute -top-3 left-0 right-0 mx-auto max-w-min`]}
          >
            {/* Change "APR" to "Finance" */}
            {cardType === "APR" ? _("Finance") : cardLabel}
          </Badge>
          {/* OFFER CARD CONTAINER */}
          <article css={[tw`flex flex-col gap-2 justify-center mx-auto`]}>
            {/* TYPE */}
            {formattedModelName && (
              <div
                css={[tw`flex flex-col items-center justify-center gap-1 mt-5`]}
              >
                <div
                  css={[
                    tw`text-center flex items-center justify-center font-semibold mx-4 !leading-[1.7rem]`,
                    formattedModelName.split("").length <= 15
                      ? tw`text-2xl`
                      : tw`text-xl`,
                  ]}
                >
                  {formattedModelName}
                </div>
                {/* VIEW DETAILS */}
                <div css={[tw`flex items-center justify-center `]}>
                  <span
                    css={[
                      tw`uppercase font-light tracking-widest text-xs text-center `,
                    ]}
                  >
                    {_("Featured Offer")}
                  </span>
                  <Icon.Chevron
                    color="red-400"
                    direction="right"
                    css={[tw`h-3 inline ml-2`]}
                  />
                </div>
              </div>
            )}

            {/* INNER CONTENT - LEASE */}
            {cardType === "Lease" && (
              <LeaseCard
                monthlyPayment={monthlyPayment!}
                dueAtSigningAmount={dueAtSigningAmount!}
                duration={duration!}
                familyVariant
              />
            )}

            {/* INNER CONTENT - APR(RENAMED TO FINANCE) */}
            {cardType === "APR" && (
              <FinanceCard termRates={termRates!} familyVariant />
            )}

            {/* INNER CONTENT - CASH */}
            {cardType === "Cash" && (
              <CashCard
                cashAmount={cashAmount!}
                type={type!}
                familyVariant={offer?.seriesImage ? true : false}
              />
            )}

            {/* INNER CONTENT - Lease Cash */}
            {cardType === "Lease Cash" && (
              <LeaseCashCard
                cashAmount={cashAmount!}
                type={formattedModelName}
                familyVariant
              />
            )}

            {/* INNER CONTENT - Finance Cash */}
            {cardType === "Finance Cash" && (
              <FinanceCashCard
                cashAmount={cashAmount!}
                type={formattedModelName}
                familyVariant
              />
            )}
            {/* EXTRA CASH AMOUNT */}
            {extraCashAmount ? (
              <p
                css={[
                  tw`block w-full font-semibold uppercase text-center text-sm`,
                ]}
              >
                {"+ $"}
                {toUsd(extraCashAmount)} {_(extraCashLabel!)}
              </p>
            ) : null}
            <div
              css={[
                tw`absolute z-50 -bottom-11 left-1/2 transform -translate-x-1/2 h-32 w-96`,
              ]}
            >
              <Image
                imageData={offer?.seriesImage || null}
                css={[tw`h-auto w-full object-contain -mt-10`]}
              />
            </div>
          </article>
        </Card>
      </Link>
    </div>
  )
}

export default FamilyOfferCard
